import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { catchError, delay, switchMap, take } from 'rxjs/operators';
import { EnumHomesActions, GetHomeConfigSuccess, GetHomesSuccess, GetHomeStatusSuccess, RemoveRoomFromHome } from '../homes/homes.action';
import { EnumModulesActions, UpdateModuleRoomSuccess } from '../modules/modules.action';
import { RoomsActions } from './rooms.action';
import { RoomsService } from './rooms.service';

@Injectable()
export class RoomsEffects {
  private readonly actions$ = inject(Actions);
  private readonly roomsService = inject(RoomsService);

  getRooms$ = createEffect(() => this.actions$.pipe(
    ofType<GetHomesSuccess>(EnumHomesActions.GetHomesSuccess),
    switchMap((action) => [RoomsActions.getRoomsSuccess({ payload: action.payload, modules: action.modules })]),
  ));

  updateConfig$ = createEffect(() => this.actions$.pipe(
    ofType<GetHomeConfigSuccess>(EnumHomesActions.GetHomeConfigSuccess),
    switchMap((action) => [RoomsActions.updateRoomsGetConfig({ payload: action.payload })]),
  ));

  updateStatus$ = createEffect(() => this.actions$.pipe(
    ofType<GetHomeStatusSuccess>(EnumHomesActions.GetHomeStatusSuccess),
    switchMap((action) => [RoomsActions.updateRoomsStatus({ payload: action.payload })]),
  ));

  setTrueTemperature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomsActions.setTrueTemperature),
      switchMap((action) =>
        this.roomsService.setTrueTemperature(action.payload).pipe(
          switchMap(() => [RoomsActions.setTrueTemperatureSuccess({ payload: action.payload })]),
          catchError(({ error }) => of(RoomsActions.setTrueTemperatureFailure({ payload: error }))),
        ),
      ),
    ),
  );

  getMeasureCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomsActions.getMeasureCsv),
      switchMap((action) =>
        this.roomsService.getMeasureCsv(action.payload).pipe(
          switchMap((t: Blob) => [RoomsActions.getMeasureCsvSuccess({ payload: t, info: action.payload })]),
          catchError(({ error }) => of(RoomsActions.getMeasureCsvFailure({ payload: error }))),
        ),
      ),
    ),
  );

  getRoomMeasureCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomsActions.getRoomMeasureCsv),
      switchMap((action) =>
        this.roomsService.getRoomMeasureCsv(action.payload).pipe(
          switchMap((t: Blob) => [RoomsActions.getRoomMeasureCsvSuccess({ payload: t, info: action.payload })]),
          catchError(({ error }) => of(RoomsActions.getRoomMeasureCsvFailure({ payload: error }))),
        ),
      ),
    ),
  );

  updateRoomName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomsActions.updateRoomName),
      switchMap((action) =>
        this.roomsService.updateRoom(action.payload, action.header).pipe(
          switchMap(() => [RoomsActions.updateRoomNameSuccess({ payload: action.payload })]),
          catchError(({ error }) => of(RoomsActions.updateRoomNameFailure({ payload: error }))),
        ),
      ),
    ),
  );

  moveModuleRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateModuleRoomSuccess>(EnumModulesActions.UpdateModuleRoomSuccess),
      switchMap((action) => {
        const id = action.payload.module_id ? action.payload.module_id : action.payload.device_id;
        const payload = { room_id: action.payload.room_id, module_id: id };

        return [RoomsActions.removeModuleFromRoom({ payload }), RoomsActions.addModuleToRoom({ payload })];
      }),
    ),
  );

  createNewRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomsActions.createNewRoom),
      switchMap((action) =>
        this.roomsService.createRoom(action.payload, action.header).pipe(
          delay(1000),
          switchMap((res) => {
            const payload = { ...action.payload, room_id: res.body.room_id };
            return [RoomsActions.createNewRoomSuccess({ payload })];
          }),
          catchError(({ error }) => of(RoomsActions.createNewRoomFailure({ payload: error }))),
        ),
      ),
    ),
  );

  deleteMeasurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomsActions.deleteMeasurements),
      switchMap((action) =>
        this.roomsService.deleteMeasurements(action.payload).pipe(
          switchMap(() => [RoomsActions.deleteMeasurementsSuccess({ payload: action.payload })]),
          catchError(({ error }) => of(RoomsActions.deleteMeasurementsFailure({ payload: error }))),
        ),
      ),
    ),
  );

  deleteRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomsActions.deleteRoom),
      switchMap((action) =>
        this.roomsService.deleteRoom(action.payload).pipe(
          switchMap(() => [
            RoomsActions.deleteRoomSuccess({ payload: action.payload }),
            new RemoveRoomFromHome(action.payload),
          ]),
          catchError(({ error }) => of(RoomsActions.deleteRoomFailure({ payload: error }))),
        ),
      ),
    ),
  );

  updateRoomOffset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomsActions.updateRoomMeasureOffset),
      switchMap((action) => {
        const apiObservables = action.payload.map((data) =>
          this.roomsService.updateRoomOffset(data).pipe(
            switchMap((res) => of(res)),
            catchError(({ error }) => of(error)),
            take(1),
          ),
        );

        return forkJoin(apiObservables).pipe(
          switchMap((results) => {
            const status = results.filter((result) => result.status !== 'ok');
            if (status.length === 0) {
              return of(RoomsActions.updateRoomMeasureOffsetSuccess({ payload: action.payload }));
            } else {
              return of(RoomsActions.updateRoomMeasureOffsetFailure({ payload: status[0] }));
            }
          }),
        );
      }),
    ),
  );
}
